import { useEffect, useState } from 'react';
import { api } from '../api.js';
import PasswordInput from '../components/PasswordInput.jsx';

export default function Login({ onLogin, notice }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Непознато устройство отвън — първо регистрация с код (миграция 115).
  const [needsDevice, setNeedsDevice] = useState(false);
  // Формата се показва едва след отговора, за да е потребителят вече
  // сложен, когато Chrome реши какво да предложи. На компютър с известен
  // потребител (Светлана / Калин) фокусът отива направо на паролата.
  const [ready, setReady] = useState(false);
  const [hasDefault, setHasDefault] = useState(false);

  useEffect(() => {
    api.get('/api/access/status')
      .then((s) => {
        setNeedsDevice(!!s.needsDevice);
        if (s.defaultUsername) {
          setUsername((cur) => cur || s.defaultUsername);
          setHasDefault(true);
        }
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  async function submit(event) {
    event?.preventDefault?.();
    setBusy(true);
    setError(null);
    try {
      onLogin(await api.post('/api/auth/login', { username, password }));
    } catch (err) {
      if (err.status === 403) setNeedsDevice(true);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (needsDevice) {
    return <RegisterDevice onDone={() => { setNeedsDevice(false); setError(null); }} error={error} />;
  }

  if (!ready) return <div className="login-wrap" />;

  return (
    <div className="login-wrap">
      <form className="login" onSubmit={submit}>
        <h1>Тефтер</h1>
        <p className="sub">Складова система · Доктор Принтер ЕООД</p>

        {notice && !error && <div className="notice">{notice}</div>}
        {error && <div className="error">{error}</div>}

        <label>
          <span>Потребител</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus={!hasDefault}
            autoComplete="username"
          />
        </label>

        <label>
          <span>Парола</span>
          <PasswordInput
            value={password}
            onChange={setPassword}
            autoFocus={hasDefault}
            autoComplete="current-password"
            onEnter={() => { if (username && password && !busy) submit(new Event('submit')); }}
          />
        </label>

        <button className="primary" disabled={busy || !username || !password}>
          {busy ? 'Влизане…' : 'Вход'}
        </button>
      </form>
    </div>
  );
}

/**
 * Регистриране на устройство с еднократен код.
 *
 * Отвън Тефтер пуска само регистрирани устройства. Кодът се генерира от
 * компютър в офиса („Служители и достъп" → „Устройства") и важи 10
 * минути. След него браузърът пази таен ключ за година.
 */
function RegisterDevice({ onDone, error: parentError }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState(() => guessName());
  const [error, setError] = useState(parentError ?? null);
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event?.preventDefault?.();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/access/register-device', { code: code.trim(), name: name.trim() });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <form className="login" onSubmit={submit}>
        <h1>Тефтер</h1>
        <p className="sub">Непознато устройство</p>
        {error && <div className="error">{error}</div>}
        <p className="hint" style={{ marginTop: 0 }}>
          Извън офиса Тефтер се отваря само от регистрирани устройства. Поискайте
          код от администратора („Служители и достъп" → „Устройства") — важи 10 минути.
        </p>
        <label>
          <span>Код</span>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoFocus autoComplete="one-time-code" autoCapitalize="characters"
            style={{ letterSpacing: '0.2em', fontSize: 18 }} maxLength={12} />
        </label>
        <label>
          <span>Име на устройството</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off"
            placeholder="напр. iPhone Калин" maxLength={80} />
        </label>
        <button className="primary" disabled={busy || code.trim().length < 4 || !name.trim()}>
          {busy ? 'Регистриране…' : 'Регистрирай устройството'}
        </button>
      </form>
    </div>
  );
}

/** Предложение за име по браузъра — „iPhone", „Android", „Windows". */
function guessName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Компютър Windows';
  if (/Mac OS X/.test(ua)) return 'Mac';
  return '';
}

/**
 * Смяна на временната парола при първо влизане.
 *
 * Паролата, дадена от администратора, я знаят двама. Докато човекът не
 * си сложи своя, сървърът не пуска нищо друго — профилът става личен
 * едва оттук нататък.
 */
export function ForcedPasswordChange({ user, onDone }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const ready = current.length > 0 && next.length >= 6 && next === repeat && next !== current;

  async function submit(event) {
    event?.preventDefault?.();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await api.put('/api/auth/password', { currentPassword: current, newPassword: next });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function izhod() {
    await api.post('/api/auth/logout').catch(() => {});
    window.location.reload();
  }

  return (
    <div className="login-wrap">
      <form className="login" onSubmit={submit}>
        <h1>Нова парола</h1>
        <p className="sub">
          {user.fullName}, паролата ви е дадена от администратор. Сложете своя —
          само вие трябва да я знаете.
        </p>
        {error && <div className="error">{error}</div>}
        <label>
          <span>Временна парола</span>
          <PasswordInput value={current} onChange={setCurrent} autoComplete="current-password" />
        </label>
        <label>
          <span>Нова парола (поне 6 знака)</span>
          <PasswordInput value={next} onChange={setNext} autoComplete="new-password" />
        </label>
        <label>
          <span>Повторете</span>
          <PasswordInput value={repeat} onChange={setRepeat} autoComplete="new-password"
            onEnter={() => submit()} />
          {repeat && next !== repeat && (
            <span className="hint" style={{ color: 'var(--danger)' }}>не съвпадат</span>
          )}
        </label>
        <button className="primary" disabled={busy || !ready}>
          {busy ? 'Записване…' : 'Запиши и продължи'}
        </button>
        <button type="button" className="link" onClick={izhod} style={{ marginTop: 8 }}>Изход</button>
      </form>
    </div>
  );
}
